import { api } from '@/api';
import { type FileMetaInfoDto } from '@/api/generator';

export async function downloadFile(file: FileMetaInfoDto) {
  const { result: blob } = await api.filesApi.getFile({ fileId: file.id });

  if (blob) {
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = file.name || `Download-${file.id}`;
    link.click();
    URL.revokeObjectURL(link.href);
  }
}

/**
 * Downloads the binary payload of a file-typed Track-it data sequence (UserDefined, MCC or XCC).
 * The backend derives the file name from the sequence name, but it is passed here as well so the
 * saved file matches what the user sees in the measurement-data table.
 */
export async function downloadDataSequenceFile(dataSequenceId: number, fileName?: string | null) {
  const { result: blob } = await api.trackItImportApi.getDataSequenceFile({ dataSequenceId });

  if (blob) {
    // A sequence name is free text and may be absent or blank. A blank download attribute is not
    // a usable fallback: a blob: URL carries no Content-Disposition, so the browser would invent
    // a name of its own.
    let name = fileName?.trim();
    if (!name) {
      name = `sequence-${dataSequenceId}.bin`;
    }

    // The browser fetches the blob: URL asynchronously, after the click has been dispatched, so
    // the URL has to outlive the current task — revoking it synchronously cancels the download.
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

export function downloadUrl(urlString: string) {
  if (URL.canParse(urlString)) {
    const link = document.createElement('a');
    link.href = urlString;
    link.download = '';
    link.click();
    URL.revokeObjectURL(link.href);
  } else {
    throw new Error(`Invalid download URL: ${urlString}`);
  }
}
